import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { COPY, LANGS, type Copy, type Lang } from './copy';

/*
 * Language dropdown in the header. The trigger shows the current language code; the
 * menu names each language in its own language. Closes on a pick, Escape, or a click
 * outside, and returns focus to the trigger.
 */

export const LangMenu = ({
  lang,
  onChoose,
  c,
}: {
  lang: Lang;
  onChoose: (l: Lang) => void;
  c: Copy;
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    itemRefs.current[LANGS.indexOf(lang)]?.focus();
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, lang]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent) => {
    const items = itemRefs.current;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="lang-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="lang-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${c.langLabel}: ${c.langName}`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg className="i" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M3.5 12h17M12 3.5c2.4 2.3 3.6 5.1 3.6 8.5s-1.2 6.2-3.6 8.5c-2.4-2.3-3.6-5.1-3.6-8.5s1.2-6.2 3.6-8.5z" />
        </svg>
        <span>{lang.toUpperCase()}</span>
        <svg className="i chev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10l5 5 5-5" />
        </svg>
      </button>
      <div
        id={menuId}
        className="lang-list"
        role="menu"
        aria-label={c.langLabel}
        data-open={open}
        onKeyDown={onMenuKey}
      >
        {LANGS.map((l, k) => (
          <button
            key={l}
            ref={(el) => {
              itemRefs.current[k] = el;
            }}
            type="button"
            role="menuitemradio"
            aria-checked={l === lang}
            lang={l}
            tabIndex={open ? 0 : -1}
            onClick={() => {
              close();
              if (l !== lang) onChoose(l);
            }}
          >
            <span className="code">{l.toUpperCase()}</span>
            {COPY[l].langName}
            <svg className="i tick" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};
